import React, { useCallback, useContext, useEffect, useState } from 'react';
// Route hooks resolve to the embedded flow's synthetic location when this page
// is rendered inside a panel, and to the real router otherwise.
import {
  useRouteParams as useParams,
  useRouteLocation as useLocation,
  useIsEmbeddedRoute,
} from '../../../../../common/embeddedFlow/embeddedRoute';
import Loader from '../../../../../common/Loader/Loader';
import ProfileCard from '../../../../../common/ProfileCard/ProfileCard';
import { ApiContext } from '../../../../../context/MyContext';
import { WorkoutDataProvider } from '../../../core/providers/WorkoutDataProvider';
import { fetchDurationById } from '../../../core/utils/assignedPlansUtils';
import { useWorkoutPlanEventTypeId } from '../../plan/eventTypes';
import WorkoutDurationDetailView from './WorkoutDurationDetailView';

function WorkoutDurationDetailPageInner() {
  const { id: userId, durationId } = useParams();
  const location = useLocation();
  // Inside a panel the host already shows a compact profile header, so the
  // full card here would be a second copy of the same facts.
  const isEmbedded = useIsEmbeddedRoute();
  const { get } = useContext(ApiContext);
  const eventTypeId = useWorkoutPlanEventTypeId();

  const durationFromState = location.state?.durationMeta?.[durationId];
  // Create flow seeds durationMeta with weekdays: [] — treat that as incomplete so we
  // refetch and show ✓/✗ from AssignedPlans instead of skipping the load.
  const hasWeekdayAssignments =
    Array.isArray(durationFromState?.weekdays) &&
    durationFromState.weekdays.length > 0;
  const [duration, setDuration] = useState(durationFromState || null);
  const [loading, setLoading] = useState(!hasWeekdayAssignments);

  const loadDuration = useCallback(async () => {
    if (!eventTypeId || !userId || !durationId) return;
    if (!hasWeekdayAssignments) setLoading(true);
    try {
      const match = await fetchDurationById(get, {
        userId,
        eventTypeId,
        durationId,
      });
      if (match) setDuration(match);
    } catch (err) {
      console.error('Failed to load workout duration:', err);
    } finally {
      setLoading(false);
    }
  }, [eventTypeId, userId, durationId, get, hasWeekdayAssignments]);

  useEffect(() => {
    loadDuration();
  }, [loadDuration]);

  return (
    <div className="flex-1 flex flex-col items-center">
      <div className="container px-2 py-4 mx-auto overflow-auto w-full">
        {!isEmbedded && (
          <ProfileCard profileId={userId} floating enableNotesTab />
        )}
        <div className={isEmbedded ? "" : "mt-4"}>
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader />
            </div>
          ) : (
            <WorkoutDurationDetailView
              duration={duration}
              userId={userId}
              onDurationChange={setDuration}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default function WorkoutDurationDetailPage() {
  return (
    <WorkoutDataProvider>
      <WorkoutDurationDetailPageInner />
    </WorkoutDataProvider>
  );
}
